import { useState, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';
import logger from '../utils/logger';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

/**
 * Custom hook for handling file processing operations
 * Manages transcription and synopsis processing, job status polling, and WebSocket updates
 */
export const useProcessing = (fetchMyProjects) => {
  const [currentJobId, setCurrentJobId] = useState(null);
  const [currentJob, setCurrentJob] = useState(null);
  const [result, setResult] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [duration, setDuration] = useState(null);
  const [eta, setEta] = useState(null);
  const [status, setStatus] = useState('');
  const [error, setError] = useState(null);
  const [showCompletionPopup, setShowCompletionPopup] = useState(false);
  const [completionPopupData, setCompletionPopupData] = useState({
    title: '',
    message: '',
    projectName: ''
  });
  const [popupCountdown, setPopupCountdown] = useState(10);
  const [showTranscriptPopup, setShowTranscriptPopup] = useState(false);
  const [isProcessingMinimized, setIsProcessingMinimized] = useState(false);

  const pollIntervalRef = useRef(null);
  const setCurrentSynopsisProjectIdRef = useRef(null);

  // Poll job status
  const pollJobStatus = useCallback(async (jobId) => {
    if (!jobId) return;

    try {
      const response = await axios.get(`${API_BASE}/status/${jobId}`);
      const jobData = response.data;

      setProgress(jobData.progress || 0);
      setProgressMessage(jobData.message || '');
      setDuration(jobData.duration_seconds);
      setEta(jobData.estimated_completion);

      if (jobData.status === 'completed') {
        setResult(jobData.result);
        setIsProcessing(false);
        setStatus('Completed');
        
        // If this is a synopsis job, set the generated synopsis
        if (jobData.result && (jobData.result.synopsis_text || jobData.result.synopsis)) {
          // This will be handled by the parent component
          logger.log('✅ Synopsis job completed');
        } else {
          setShowTranscriptPopup(true);
        }
        
        // Refresh projects after completion
        setTimeout(() => {
          logger.log('🔄 Polling completion: Refreshing projects after delay...');
          fetchMyProjects(false);
        }, 2000);
        
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
      } else if (jobData.status === 'failed') {
        setError(jobData.error || 'Processing failed');
        setIsProcessing(false);
        setStatus('Failed');
        
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
      }
    } catch (error) {
      logger.error('Error polling job status:', error);
      setError('Failed to get job status');
      setIsProcessing(false);
      
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }
  }, [fetchMyProjects]);

  // Track if this is a synopsis job to skip polling
  const isSynopsisJobRef = useRef(false);

  // Start polling when job is created (skip for synopsis jobs - use WebSocket instead)
  useEffect(() => {
    if (currentJobId && isProcessing) {
      // Skip polling for synopsis jobs - rely on WebSocket for updates
      if (isSynopsisJobRef.current) {
        logger.log('✅ Synopsis job created, waiting for WebSocket updates...');
        
        // Optional: Set a timeout to close modal if no updates after X minutes
        // This is a safety net in case WebSocket fails
        const safetyTimeout = setTimeout(() => {
          logger.log('⚠️ No updates received, closing modal after 10 minutes');
          setIsProcessing(false);
        }, 600000); // 10 minutes
        
        return () => {
          clearTimeout(safetyTimeout);
        };
      }
      
      // For transcription jobs, continue with polling
      pollIntervalRef.current = setInterval(() => {
        pollJobStatus(currentJobId);
      }, 2000); // Poll every 2 seconds

      return () => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
      };
    }
  }, [currentJobId, isProcessing, pollJobStatus]);

  // Handle WebSocket message
  const handleWebSocketMessage = useCallback((data) => {
    logger.log('📨 WebSocket message received:', data);
    
    if (data.type === 'job_update' && data.job_id === currentJobId) {
      const updateData = data.data;
      
      // Update progress and status from WebSocket
      if (updateData.progress !== undefined) {
        setProgress(updateData.progress);
      }
      if (updateData.message) {
        setProgressMessage(updateData.message);
      }
      if (updateData.status === 'completed') {
        setResult(updateData.result);
        setIsProcessing(false);
        setStatus('Completed');
        isSynopsisJobRef.current = false; // Reset flag
        logger.log(`✅ Job ${currentJobId} completed via WebSocket.`);
        
        // If this is a synopsis job, set the generated synopsis and project ID
        if (updateData.result && (updateData.result.synopsis_text || updateData.result.synopsis)) {
          logger.log('📝 Synopsis generated via WebSocket');
          
          // Extract project ID from result if available
          const projectId = updateData.result.project_id || updateData.result.id;
          if (projectId && setCurrentSynopsisProjectIdRef.current) {
            setCurrentSynopsisProjectIdRef.current(projectId);
          }
        } else {
          // Show transcript popup for non-synopsis jobs
          setShowTranscriptPopup(true);
        }
        
        // Refresh projects after a short delay to ensure backend processing is complete
        setTimeout(() => {
          logger.log('🔄 WebSocket completion: Refreshing projects after delay...');
          fetchMyProjects(false); // Smart refresh - Redis cache automatically invalidated by backend
        }, 2000);
      } else if (updateData.status === 'failed') {
        setError(updateData.error || 'Processing failed');
        setIsProcessing(false);
        setStatus('Failed');
        isSynopsisJobRef.current = false; // Reset flag
        logger.log(`❌ Job ${currentJobId} failed via WebSocket.`);
      }
    } else if (data.type === 'initial_status' && data.job_id === currentJobId) {
      // Handle initial status when connecting to existing job
      const jobData = data.data;
      setProgress(jobData.progress || 0);
      setProgressMessage(jobData.message || '');
      setDuration(jobData.duration_seconds);
      setEta(jobData.estimated_completion);
      
      if (jobData.status === 'completed') {
        setResult(jobData.result);
        setIsProcessing(false);
        setStatus('Completed');
        isSynopsisJobRef.current = false; // Reset flag
        
        // If this is a synopsis job, set the generated synopsis and project ID
        if (jobData.result && (jobData.result.synopsis_text || jobData.result.synopsis)) {
          logger.log('📝 Synopsis initial status completed');
          
          // Extract project ID from result if available
          const projectId = jobData.result.project_id || jobData.result.id;
          if (projectId && setCurrentSynopsisProjectIdRef.current) {
            setCurrentSynopsisProjectIdRef.current(projectId);
          }
        } else {
          setShowTranscriptPopup(true);
        }
        
        // Refresh projects
        setTimeout(() => {
          logger.log('🔄 Initial status completion: Refreshing projects after delay...');
          fetchMyProjects(false);
        }, 2000);
      } else if (jobData.status === 'failed') {
        setError(jobData.error || 'Processing failed');
        setIsProcessing(false);
        setStatus('Failed');
        isSynopsisJobRef.current = false; // Reset flag
      }
    }
  }, [currentJobId, fetchMyProjects]);

  // Process transcription files
  // inputSource: 'upload' | 'drive' – which source to use (so we only send one)
  const handleProcessFiles = useCallback(async (
    projectName,
    selectedFiles,
    driveUrl,
    settings,
    knpFile,
    validateForm,
    assigneeId = null,
    inputSource = 'upload'
  ) => {
    // Validate form before processing
    if (!validateForm()) {
      return;
    }

    setError(null);
    setResult(null);
    setIsProcessing(true);
    setIsProcessingMinimized(false);
    setProgress(0);
    setProgressMessage('Processing...');
    setDuration(null);
    setEta(null);
    
    try {
      let jobId;
      
      if (inputSource === 'upload' && selectedFiles.length > 0) {
        // Process uploaded file (single file per project)
        const file = selectedFiles[0];
        const formData = new FormData();
        formData.append('file', file);
        formData.append('project_name', projectName);
        formData.append('language_code', settings.language);
        formData.append('boost', settings.boost);
        if (knpFile) {
          formData.append('phrase_file', knpFile);
        } else if (settings.phrases) {
          formData.append('phrases', settings.phrases);
        }
        if (assigneeId) formData.append('assignee_id', assigneeId);
        
        const response = await axios.post(`${API_BASE}/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setProgress(percentCompleted);
            setProgressMessage(`Uploading: ${percentCompleted}%`);
          },
          timeout: 300000, // 5 minutes timeout for upload
        });

        jobId = response.data.job_id;
      } else if (inputSource === 'drive' && driveUrl) {
        // Process Google Drive URL (single file per project)
        const formData = new FormData();
        formData.append('drive_url', driveUrl);
        formData.append('project_name', projectName);
        formData.append('language_code', settings.language);
        formData.append('boost', settings.boost);
        if (knpFile) {
          formData.append('phrase_file', knpFile);
        } else if (settings.phrases) {
          formData.append('phrases', settings.phrases);
        }
        if (assigneeId) formData.append('assignee_id', assigneeId);
        
        const response = await axios.post(`${API_BASE}/drive`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 300000, // 5 minutes timeout
        });

        jobId = response.data.job_id;
      } else {
        setError('Please select a file (Upload) or enter a Google Drive URL.');
        setIsProcessing(false);
        return;
      }

      // Mark as transcription job (not synopsis)
      isSynopsisJobRef.current = false;
      
      setCurrentJob(jobId);
      setCurrentJobId(jobId);
      setProgressMessage('Starting transcription...');
      pollJobStatus(jobId);
    } catch (error) {
      logger.error('Processing error:', error);
      if (axios.isCancel(error)) {
        setError('Upload cancelled.');
      } else if (error.code === 'ECONNABORTED') {
        setError('Upload timed out after 5 minutes. Please try a smaller file or a faster connection.');
      } else {
        setError('Upload failed: ' + (error.response?.data?.detail || error.message));
      }
      setIsProcessing(false);
    }
  }, [pollJobStatus]);

  // Generic content processing (synopsis, keyword, etc.)
  const handleContentProcessing = useCallback(async (
    featureType, // 'synopsis' or 'keyword' or 'transcription'
    youtubeUrl,
    youtubeVideoInfo,
    selectedPromptVersionForUse,
    setGeneratedContent,
    setCurrentProjectId,
    onRedirect,
    redirectPath, // e.g., 'synopsis-projects' or 'keyword-projects'
    showName = null,
    mp4File = null,
    projectType = null, // e.g., 'synopsis_generation', 'keyword_generation'
    comparisonText = null,
    assigneeId = null, // keyword only: PM/admin can assign at creation
    knpFile = null, // transcription only: optional phrase file
    languageCode = 'ko-KR' // transcription only: e.g. 'ko-KR', 'en-US'
  ) => {
    // Validate that either YouTube URL or MP4 file is provided
    const hasYoutubeUrl = youtubeUrl && youtubeUrl.trim();
    const hasMp4File = mp4File && mp4File.name;
    
    if (!hasYoutubeUrl && !hasMp4File) {
      alert('Please provide either a YouTube URL or upload an MP4 file');
      return;
    }

    // Determine project type if not provided
    const finalProjectType = projectType || `${featureType}_generation`;

    // Video info is optional - backend will fetch it if not provided
    setError(null);
    setResult(null);
    setIsProcessing(true);
    setIsProcessingMinimized(false);
    setProgress(0);
    setProgressMessage(`Generating ${featureType}...`);

    try {
      const token = localStorage.getItem('token');
      
      const formData = new FormData();
      // Always append YouTube URL (empty string if not provided) to satisfy FastAPI Form validation
      formData.append('youtube_url', hasYoutubeUrl ? youtubeUrl.trim() : '');
      formData.append('project_type', finalProjectType);
      
      if (comparisonText) {
        formData.append('comparison_synopsis_text', comparisonText);
      }
      
      // Include prompt version if selected
      if (selectedPromptVersionForUse) {
        formData.append('prompt_version', selectedPromptVersionForUse);
      }
      
      // Include show name if provided (skip if "Other")
      if (showName && showName !== 'Other') {
        formData.append('show_name', showName);
      }
      
      // Include MP4 file if provided
      if (mp4File) {
        formData.append('mp4_file', mp4File);
      }

      // Keyword, Synopsis, or Transcription: assignee at creation (PM/admin)
      if ((featureType === 'keyword' || featureType === 'synopsis' || featureType === 'transcription') && assigneeId) {
        formData.append('assignee_id', assigneeId);
      }

      // Transcription only: optional KNP phrase file and language
      if (featureType === 'transcription') {
        if (knpFile) {
          formData.append('phrase_file', knpFile);
        }
        formData.append('language_code', languageCode || 'ko-KR');
      }

      const apiUrl = `${API_BASE}/${featureType}/youtube`;
      const response = await axios.post(apiUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        timeout: 120000, // 2 minutes timeout
      });

      const jobId = response.data.job_id;
      const projectId = response.data.project_id; // Extract project ID if available
      console.log('🆔 DEBUG: Job ID extracted:', jobId);
      console.log('🆔 DEBUG: Project ID extracted:', projectId);
      console.log('🆔 DEBUG: Job ID type:', typeof jobId);
      
      // Mark as content generation job to skip polling
      isSynopsisJobRef.current = true;
      
      setCurrentJob(jobId);
      setCurrentJobId(jobId);
      setProgressMessage('Processing video in background...');
      
      // Store setter in ref for WebSocket access
      if (setCurrentProjectId) {
        setCurrentSynopsisProjectIdRef.current = setCurrentProjectId;
        // Set project ID if available
        if (projectId) {
          setCurrentProjectId(projectId);
        }
      }
      
      // Redirect to projects list after a short delay
      setTimeout(() => {
        if (onRedirect && redirectPath) {
          onRedirect(redirectPath);
        }
        // Close modal - user can see progress in projects list
        setIsProcessing(false);
        // Refresh projects to show the new one
        fetchMyProjects(false);
      }, 1500);
      // Poll for job completion
      pollJobStatus(jobId);
    } catch (error) {
      logger.error(`${featureType} processing error:`, error);
      setError(`Failed to start ${featureType} generation: ` + (error.response?.data?.detail || error.message));
      setIsProcessing(false);
      isSynopsisJobRef.current = false; // Reset flag on error
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollJobStatus]);

  // Process synopsis generation (backward compatibility wrapper)
  const handleSynopsisProcessing = useCallback(async (
    youtubeUrl,
    youtubeVideoInfo,
    selectedPromptVersionForUse,
    setGeneratedSynopsis,
    setCurrentSynopsisProjectId,
    onRedirect,
    showName = null,
    mp4File = null,
    projectType = 'synopsis_generation',
    comparisonSynopsisText = null,
    assigneeId = null
  ) => {
    return handleContentProcessing(
      'synopsis',
      youtubeUrl,
      youtubeVideoInfo,
      selectedPromptVersionForUse,
      setGeneratedSynopsis,
      setCurrentSynopsisProjectId,
      onRedirect,
      'synopsis-projects',
      showName,
      mp4File,
      projectType,
      comparisonSynopsisText,
      assigneeId
    );
  }, [handleContentProcessing]);

  // Copy transcript to clipboard
  const copyToClipboard = useCallback((text) => {
    navigator.clipboard.writeText(text);
    alert('Transcript copied to clipboard!');
  }, []);

  // Download transcript
  const downloadTranscript = useCallback(() => {
    if (result && result.transcript) {
      const blob = new Blob([result.transcript], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${result.original_filename || 'transcript'}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  }, [result]);

  return {
    // State
    currentJobId,
    currentJob,
    result,
    isProcessing,
    progress,
    progressMessage,
    duration,
    eta,
    status,
    error,
    showCompletionPopup,
    completionPopupData,
    popupCountdown,
    showTranscriptPopup,
    isProcessingMinimized,
    
    // Setters
    setCurrentJobId,
    setCurrentJob,
    setResult,
    setIsProcessing,
    setProgress,
    setProgressMessage,
    setDuration,
    setEta,
    setStatus,
    setError,
    setShowCompletionPopup,
    setCompletionPopupData,
    setPopupCountdown,
    setShowTranscriptPopup,
    setIsProcessingMinimized,
    
    // Functions
    pollJobStatus,
    handleWebSocketMessage,
    handleProcessFiles,
    handleSynopsisProcessing,
    handleContentProcessing, // Generic function for all content types
    copyToClipboard,
    downloadTranscript,
  };
};

